import React, { useMemo, useState } from 'react';
import {
  Button,
  Confirm,
  Form,
  Input,
  Message,
  Radio,
} from 'semantic-ui-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { postWithAuth } from 'src/api';
import './ProjectTransfer.module.css';

type TransferMode = 'copy' | 'move';

type UserProject = {
  id: string;
  ref_id: string;
  name: string;
  last_modified: string | null;
  permission: string;
  in_work: boolean;
};

type UserProjectsResponse = {
  email: string;
  projects: UserProject[];
};

type TransferResponse = {
  transferred: string[];
  failed: string[];
};

const normaliseEmail = (email: string) => email.trim().toLowerCase();

function useUserProjects(email: string) {
  return useQuery<UserProjectsResponse, { detail?: string }>({
    queryKey: ['admin', 'userProjects', email],
    queryFn: () => postWithAuth('admin/user_projects', { email }),
    enabled: email !== '',
    retry: false,
  });
}

function UserEmailField({ label, value, onChange, onLoad, loading }) {
  return (
    <Form
      onSubmit={() => {
        if (value.trim() !== '') {
          onLoad();
        }
      }}
    >
      <Form.Field>
        <label>{label}</label>
        <Input
          type="email"
          value={value}
          placeholder="user@example.com"
          onChange={(e, { value }) => onChange(value)}
          action={{
            primary: true,
            content: 'Load',
            type: 'submit',
            loading,
            disabled: value.trim() === '',
          }}
        />
      </Form.Field>
    </Form>
  );
}

function ProjectList({
  title,
  query,
  email,
  selected = null,
  onItemClick = null,
  sharedRefIds = null,
  search,
  setSearch,
}) {
  const { data, error, isFetching } = query;

  const projects: UserProject[] = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return (data?.projects ?? []).filter((p) =>
      p.name.toLowerCase().includes(searchText)
    );
  }, [data, search]);

  let content;
  if (email === '') {
    content = (
      <div className="project-transfer-list-message">
        Enter an email address and press Load
      </div>
    );
  } else if (isFetching) {
    content = <div className="project-transfer-list-message">Loading...</div>;
  } else if (error) {
    content = (
      <div className="project-transfer-list-message">
        {error.detail ?? 'Unable to load projects for this user'}
      </div>
    );
  } else if (projects.length === 0) {
    content = (
      <div className="project-transfer-list-message">No projects found</div>
    );
  } else {
    content = projects.map((project, index) => {
      const classNames = ['project-transfer-item'];
      if (onItemClick) {
        classNames.push('selectable');
      }
      if (selected?.has(project.ref_id)) {
        classNames.push('selected');
      }

      const meta = [project.last_modified?.split('T')[0]];
      if (project.permission !== 'admin') {
        meta.push(project.permission);
      }
      if (sharedRefIds?.has(project.ref_id)) {
        meta.push('shared');
      }

      return (
        <div
          key={project.ref_id}
          className={classNames.join(' ')}
          title={project.name}
          onClick={
            onItemClick ? (e) => onItemClick(e, projects, index) : undefined
          }
        >
          <span className="project-transfer-item-name">{project.name}</span>
          <span className="project-transfer-item-meta">
            {meta.filter(Boolean).join(' · ')}
          </span>
        </div>
      );
    });
  }

  return (
    <div>
      <div className="project-transfer-list-header">
        <h4>{title}</h4>
        {data ? <span>{data.projects.length} projects</span> : null}
      </div>
      <Input
        fluid
        size="small"
        value={search}
        placeholder="Search projects..."
        onChange={(e, { value }) => setSearch(value)}
        icon={{
          name: 'cancel',
          link: true,
          onClick: () => setSearch(''),
        }}
      />
      <div className="project-transfer-list">{content}</div>
    </div>
  );
}

export function ProjectTransfer() {
  const qc = useQueryClient();

  const [sourceInput, setSourceInput] = useState('');
  const [targetInput, setTargetInput] = useState('');
  const [sourceEmail, setSourceEmail] = useState('');
  const [targetEmail, setTargetEmail] = useState('');
  const [sourceSearch, setSourceSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [mode, setMode] = useState<TransferMode>('copy');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lastClicked, setLastClicked] = useState<string | null>(null);
  const [pending, setPending] = useState<string[] | null>(null);
  const [result, setResult] = useState<{
    error: boolean;
    message: string;
  } | null>(null);

  const sourceQuery = useUserProjects(sourceEmail);
  const targetQuery = useUserProjects(targetEmail);

  const clearSelection = () => {
    setSelected(new Set());
    setLastClicked(null);
  };

  const loadSource = (email: string) => {
    setSourceEmail(normaliseEmail(email));
    clearSelection();
    setResult(null);
  };

  const loadTarget = (email: string) => {
    setTargetEmail(normaliseEmail(email));
    setResult(null);
  };

  const swapUsers = () => {
    setSourceInput(targetInput);
    setTargetInput(sourceInput);
    setSourceEmail(targetEmail);
    setTargetEmail(sourceEmail);
    setSourceSearch(targetSearch);
    setTargetSearch(sourceSearch);
    clearSelection();
    setResult(null);
  };

  const onSourceItemClick = (e, projects: UserProject[], index: number) => {
    const refId = projects[index].ref_id;
    const next = new Set(selected);
    const lastIndex = projects.findIndex((p) => p.ref_id === lastClicked);

    if (e.shiftKey && lastIndex !== -1) {
      // select the range between the last clicked item and this one
      const [start, end] = [lastIndex, index].sort((a, b) => a - b);
      projects.slice(start, end + 1).forEach((p) => next.add(p.ref_id));
    } else if (next.has(refId)) {
      next.delete(refId);
    } else {
      next.add(refId);
    }

    setSelected(next);
    setLastClicked(refId);
  };

  const sourceProjects = sourceQuery.data?.projects ?? [];
  const filteredSourceRefIds = sourceProjects
    .filter((p) =>
      p.name.toLowerCase().includes(sourceSearch.trim().toLowerCase())
    )
    .map((p) => p.ref_id);
  const selectedRefIds = sourceProjects
    .map((p) => p.ref_id)
    .filter((refId) => selected.has(refId));
  const sharedRefIds = new Set(sourceProjects.map((p) => p.ref_id));

  const sameUser =
    sourceQuery.data != null &&
    targetQuery.data != null &&
    sourceQuery.data.email.toLowerCase() ===
      targetQuery.data.email.toLowerCase();
  const usersReady =
    sourceQuery.isSuccess && targetQuery.isSuccess && !sameUser;

  const transfer = useMutation<TransferResponse, { detail?: string }, string[]>(
    {
      mutationFn: (refIds) =>
        postWithAuth('admin/transfer_projects', {
          source_email: sourceEmail,
          target_email: targetEmail,
          mode,
          ref_ids: refIds,
        }),
      onSuccess: ({ transferred, failed }) => {
        const action = mode === 'copy' ? 'Copied' : 'Moved';
        let message = `${action} ${transferred.length} project(s) to ${targetQuery.data?.email}.`;
        if (failed.length > 0) {
          message += ` Failed: ${failed.join(', ')}`;
        }
        setResult({ error: failed.length > 0, message });
      },
      onError: (error) => {
        setResult({
          error: true,
          message: error?.detail ?? 'Failed to transfer the projects',
        });
      },
      onSettled: () => {
        clearSelection();
        qc.invalidateQueries({ queryKey: ['admin', 'userProjects'] });
      },
    }
  );

  const confirmMessage = pending
    ? `${mode === 'copy' ? 'Copy' : 'Move'} ${pending.length} project(s) ` +
      `from ${sourceQuery.data?.email} to ${targetQuery.data?.email}?` +
      (mode === 'move'
        ? ` ${sourceQuery.data?.email} will lose access to the moved projects.`
        : '')
    : '';

  return (
    <div>
      <div className="project-transfer-users">
        <UserEmailField
          label="Source user email"
          value={sourceInput}
          onChange={setSourceInput}
          onLoad={() => loadSource(sourceInput)}
          loading={sourceQuery.isFetching}
        />
        <Button
          icon="exchange"
          type="button"
          title="Swap source and target users"
          className="project-transfer-swap"
          onClick={swapUsers}
        />
        <UserEmailField
          label="Target user email"
          value={targetInput}
          onChange={setTargetInput}
          onLoad={() => loadTarget(targetInput)}
          loading={targetQuery.isFetching}
        />
      </div>

      <Form style={{ marginTop: '1rem' }}>
        <Form.Group inline>
          <label>Transfer mode</label>
          <Form.Field>
            <Radio
              label="Copy (source user keeps the originals)"
              checked={mode === 'copy'}
              onChange={() => setMode('copy')}
            />
          </Form.Field>
          <Form.Field>
            <Radio
              label="Move (source user loses access)"
              checked={mode === 'move'}
              onChange={() => setMode('move')}
            />
          </Form.Field>
        </Form.Group>
      </Form>

      {sameUser ? (
        <Message warning>The source and target users must be different</Message>
      ) : null}
      {result ? (
        <Message
          positive={!result.error}
          negative={result.error}
          onDismiss={() => setResult(null)}
        >
          {result.message}
        </Message>
      ) : null}

      <div className="project-transfer-lists">
        <div>
          <ProjectList
            title="Source Projects"
            query={sourceQuery}
            email={sourceEmail}
            selected={selected}
            onItemClick={onSourceItemClick}
            search={sourceSearch}
            setSearch={setSourceSearch}
          />
          <div className="project-transfer-footer">
            <Button
              type="button"
              disabled={selected.size === 0}
              onClick={clearSelection}
            >
              Clear Selection ({selectedRefIds.length})
            </Button>
          </div>
        </div>

        <div className="project-transfer-buttons">
          <Button
            icon="angle double right"
            type="button"
            title={`${mode === 'copy' ? 'Copy' : 'Move'} all listed projects`}
            disabled={
              !usersReady ||
              filteredSourceRefIds.length === 0 ||
              transfer.isLoading
            }
            onClick={() => setPending(filteredSourceRefIds)}
          />
          <Button
            icon="angle right"
            type="button"
            title={`${mode === 'copy' ? 'Copy' : 'Move'} selected projects`}
            disabled={
              !usersReady || selectedRefIds.length === 0 || transfer.isLoading
            }
            loading={transfer.isLoading}
            onClick={() => setPending(selectedRefIds)}
          />
        </div>

        <ProjectList
          title="Target Projects"
          query={targetQuery}
          email={targetEmail}
          sharedRefIds={sharedRefIds}
          search={targetSearch}
          setSearch={setTargetSearch}
        />
      </div>

      <Confirm
        open={pending != null}
        header="Confirm project transfer"
        content={confirmMessage}
        confirmButton={mode === 'copy' ? 'Copy' : 'Move'}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          transfer.mutate(pending);
          setPending(null);
        }}
      />
    </div>
  );
}
